import { ArchitectureParseError } from "../errors";
import type { ConsentSurface, Permission, RiskSeverity } from "../types";
import { asString, asStringArray } from "../v3/parse-bullets-v3";

// Coerce js-yaml output into structured Permission[]. New in archviz/v0.4.
// Mirrors the parseRisks pattern: strict-mode throws on malformed entries;
// non-strict silently drops them.

const CONSENT_SURFACE_VALUES: ReadonlySet<ConsentSurface> = new Set([
  "delegated",
  "application",
  "other",
]);

const SEVERITY_VALUES: ReadonlySet<RiskSeverity> = new Set(["info", "warning", "critical"]);

interface ParseContext {
  scope: string;
  strict: boolean;
}

export function parsePermissions(value: unknown, ctx: ParseContext): Permission[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) return [];
  const out: Permission[] = [];
  for (let i = 0; i < value.length; i += 1) {
    const perm = coercePermission(value[i], { ...ctx, scope: `${ctx.scope}.permissions[${i}]` });
    if (perm) out.push(perm);
  }
  return out;
}

function coercePermission(value: unknown, ctx: ParseContext): Permission | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    if (ctx.strict) {
      throw new ArchitectureParseError(
        `${ctx.scope}: expected a permission object (scope, consent_surface, severity, what_it_enables, what_breaks).`,
      );
    }
    return null;
  }
  const rec = value as Record<string, unknown>;
  const scope = asString(rec.scope) ?? "";
  if (scope === "") {
    if (ctx.strict) {
      throw new ArchitectureParseError(
        `${ctx.scope}: \`scope\` is required and must be non-empty.`,
      );
    }
    return null;
  }
  const rawConsent = (asString(rec.consent_surface) ?? "").toLowerCase();
  const consent_surface: ConsentSurface = CONSENT_SURFACE_VALUES.has(rawConsent as ConsentSurface)
    ? (rawConsent as ConsentSurface)
    : "other";
  const rawSeverity = (asString(rec.severity) ?? "info").toLowerCase();
  const severity: RiskSeverity = SEVERITY_VALUES.has(rawSeverity as RiskSeverity)
    ? (rawSeverity as RiskSeverity)
    : "info";
  const what_it_enables = asString(rec.what_it_enables) ?? "";
  const what_breaks = asString(rec.what_breaks) ?? "";
  if (ctx.strict && (what_it_enables === "" || what_breaks === "")) {
    throw new ArchitectureParseError(
      `${ctx.scope} (\`${scope}\`): \`what_it_enables\` and \`what_breaks\` are required and must be non-empty.`,
    );
  }
  const endpoints = asStringArray(rec.endpoints);
  return {
    scope,
    consent_surface,
    severity,
    what_it_enables,
    what_breaks,
    endpoints: endpoints.length > 0 ? endpoints : undefined,
  };
}
